import { useState } from 'react';
import {
  Briefcase,
  Building2,
  Calendar,
  Check,
  Copy,
  Eye,
  Mail,
  MapPin,
  MoreHorizontal,
  Phone,
  TrendingUp,
  UserCheck,
  UserX,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatDate } from '@/lib/date';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { useCanModifyEmployee, useIsSuperAdmin, usePermission } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import type { EmployeeListItem } from '@/types/employee';
import {
  deriveDisplayedEmployeeStatus,
  type DisplayedEmployeeStatus,
} from '@/features/people/lib/employee-status';
import { Link } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';

interface EmployeeCardProps {
  employee: EmployeeListItem;
  onImpersonate?: (id: string) => void;
  onDeactivate?: (id: string) => void;
  onDelete?: (id: string) => void;
}

function getInitials(firstName: string | null | undefined, lastName: string | null | undefined): string {
  const f = firstName?.charAt(0) || '';
  const l = lastName?.charAt(0) || '';
  return `${f}${l}`.toUpperCase() || '?';
}

function getStatusConfig(displayed: DisplayedEmployeeStatus) {
  switch (displayed) {
    case 'probation':
      return { label: 'Probation', className: 'es-probation' };
    case 'offboarding':
      return {
        label: 'Offboarding',
        className: 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300',
      };
    case 'deactivated':
      return {
        label: 'Deactivated',
        className: 'es-terminated' };
    case 'onboarding':
      return { label: 'Onboarding', className: 'es-onboarding' };
    case 'active':
    default:
      return null;
  }
}

// Editorial Hygge — V2 Soft Band. Palette lives as plain CSS classes in globals.css
// (`.eb-*` for header gradient, `.ec-border-*` for theme border, `.ec-avatar-*` for avatar fallback).
const cardThemes = [
  { gradient: 'eb-honey', avatar: 'ec-avatar-honey', border: 'ec-border-honey' },
  { gradient: 'eb-sage',  avatar: 'ec-avatar-sage',  border: 'ec-border-sage'  },
  { gradient: 'eb-terra', avatar: 'ec-avatar-terra', border: 'ec-border-terra' },
  { gradient: 'eb-dusk',  avatar: 'ec-avatar-dusk',  border: 'ec-border-dusk'  },
  { gradient: 'eb-plum',  avatar: 'ec-avatar-plum',  border: 'ec-border-plum'  },
  { gradient: 'eb-stone', avatar: 'ec-avatar-stone', border: 'ec-border-stone' },
];

function getCardTheme(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return cardThemes[Math.abs(hash) % cardThemes.length];
}

function formatEmploymentType(type: string | null | undefined): string {
  if (!type) return '';
  const labels: Record<string, string> = {
    full_time: 'Full-time',
    part_time: 'Part-time',
    contract: 'Contract',
    intern: 'Intern',
  };
  return labels[type] || type;
}

function CopyButton({
  value,
  label,
  className,
}: {
  value: string;
  label?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Failed to copy - silently fail
    }
  };

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            className={cn(
              'group/btn inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer',
              className
            )}
            onClick={handleCopy}
          >
            {label && <span className="truncate">{label}</span>}
            {copied ? (
              <Check className="h-3.5 w-3.5 text-[#6F8A57] dark:text-emerald-400 shrink-0" />
            ) : (
              <Copy className="h-3.5 w-3.5 opacity-0 group-hover/btn:opacity-100 transition-opacity shrink-0" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {copied ? 'Copied!' : 'Click to copy'}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Fact chip — compact, with more visual weight than a plain gray row.
function InfoChip({ icon: Icon, children }: { icon: typeof Building2; children: React.ReactNode }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-muted/60 px-2.5 py-1 text-xs font-medium text-foreground/80">
      <Icon className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className="truncate">{children}</span>
    </span>
  );
}

export function EmployeeCard({
  employee,
  onImpersonate,
  onDeactivate,
  onDelete,
}: EmployeeCardProps) {
  const formatName = useEmployeeNameFormatter();
  const fullName = formatName({ firstName: employee.firstName, lastName: employee.lastName });
  const statusConfig = getStatusConfig(deriveDisplayedEmployeeStatus(employee));
  const theme = getCardTheme(fullName);
  const canModify = useCanModifyEmployee();
  const isSuperAdmin = useIsSuperAdmin();
  const canImpersonate = usePermission('auth.impersonate');
  const showImpersonate =
    !!onImpersonate && canImpersonate && employee.status !== 'terminated';

  return (
    <div className={cn(
      'group relative overflow-hidden rounded-2xl border bg-card transition-all duration-300 h-full flex flex-col',
      'hover:shadow-xl hover:shadow-black/[0.08] dark:hover:shadow-black/30',
      theme.border
    )}>
      {/* Header with avatar - soft gradient background */}
      <div className={cn(
        'relative px-6 pt-6 pb-4',
        theme.gradient
      )}>
        {/* Actions menu - top right (only if user has any action permissions) */}
        {(showImpersonate || onDeactivate || (onDelete && isSuperAdmin)) && (
        <div className="absolute right-3 top-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full bg-white/60 dark:bg-black/20 backdrop-blur-sm hover:bg-white/80 dark:hover:bg-black/30"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              {showImpersonate && (
                <DropdownMenuItem
                  onClick={() => onImpersonate?.(employee.id)}
                  className="gap-2"
                >
                  <Eye className="h-4 w-4" />
                  Login as
                </DropdownMenuItem>
              )}
              {showImpersonate && (onDeactivate || (onDelete && isSuperAdmin)) && (
                <div className="my-1 h-px bg-border" />
              )}
              {onDeactivate && (
                <>
                  {employee.status === 'terminated' ? (
                    <DropdownMenuItem
                      onClick={() => {
                        if (canModify) {
                          onDeactivate(employee.id);
                        }
                      }}
                      className={cn(
                        'gap-2 text-green-600 dark:text-green-400 focus:bg-green-50 dark:focus:bg-green-950 focus:text-green-700 dark:focus:text-green-300',
                        !canModify && 'opacity-60 cursor-not-allowed hover:bg-transparent'
                      )}
                    >
                      <UserCheck className="h-4 w-4" />
                      Reactivate
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      variant="destructive"
                      onClick={() => {
                        if (canModify) {
                          onDeactivate(employee.id);
                        }
                      }}
                      className={cn(
                        'gap-2',
                        !canModify && 'opacity-60 cursor-not-allowed hover:bg-transparent hover:text-destructive/60'
                      )}
                    >
                      <UserX className="h-4 w-4" />
                      Deactivate
                    </DropdownMenuItem>
                  )}
                </>
              )}
              {onDelete && isSuperAdmin && (
                <>
                  {onDeactivate && <div className="my-1 h-px bg-border" />}
                  <DropdownMenuItem
                    variant="destructive"
                    onClick={() => {
                      onDelete(employee.id);
                    }}
                    className="gap-2 text-red-600 dark:text-red-400 focus:bg-red-50 dark:focus:bg-red-950 focus:text-red-700 dark:focus:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        )}

        {/* Google account badge — top left, only when connected */}
        {employee.user?.googleId && (
          <div className="absolute left-4 top-4">
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="h-5 w-5 rounded-full flex items-center justify-center bg-[#6F8A57] dark:bg-emerald-500 text-white">
                    <Check className="h-3 w-3 stroke-3" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right" className="text-xs">
                  Google Account connected
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}

        {/* Avatar - centered and large */}
        <div className="flex flex-col items-center">
          <Link
            to="/employees/$id"
            params={{ id: employee.id }}
            className="relative hover:opacity-80 transition-opacity"
          >
            <Avatar className="h-20 w-20 ring-4 ring-white/80 dark:ring-white/10 shadow-lg">
              {employee.avatarUrl ? (
                <AvatarImage src={employee.avatarUrl} alt={fullName} className="object-cover" />
              ) : null}
              <AvatarFallback className={cn('text-white text-xl font-semibold', theme.avatar)}>
                {getInitials(employee.firstName, employee.lastName)}
              </AvatarFallback>
            </Avatar>
            {employee.status === 'active' && (
              <div className="absolute bottom-0.5 right-0.5 h-4 w-4 rounded-full border-[3px] border-white dark:border-gray-900 bg-[#6F8A57] dark:bg-emerald-500" />
            )}
          </Link>

          {/* Name and position */}
          <div className="mt-3 text-center w-full">
            <Link
              to="/employees/$id"
              params={{ id: employee.id }}
              className="block text-lg font-semibold text-foreground truncate hover:text-primary hover:underline w-full"
            >
              {fullName}
            </Link>
            <p className="mt-0.5 text-sm text-muted-foreground truncate">
              {employee.position?.title || 'No position'}
            </p>
            {statusConfig && (
              <span className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium mt-2',
                statusConfig.className
              )}>
                {statusConfig.label}
                {employee.status === 'probation' && employee.probationEndDate && (
                  <span className="opacity-90 font-normal">
                    · until {formatDate(employee.probationEndDate, 'monthDay')}
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Main content — scannable fact chips (variant A) */}
      <div className="px-5 py-4 flex-1 flex flex-col gap-3">
        {/* Fact chips */}
        <div className="flex flex-wrap justify-center gap-1.5">
          {employee.department?.name && (
            <InfoChip icon={Building2}>{employee.department.name}</InfoChip>
          )}
          {employee.personalInfo?.address && (
            <InfoChip icon={MapPin}>{employee.personalInfo.address}</InfoChip>
          )}
          {employee.seniorityLevel?.name && (
            <InfoChip icon={TrendingUp}>{employee.seniorityLevel.name}</InfoChip>
          )}
          {employee.employmentType && (
            <InfoChip icon={Briefcase}>{formatEmploymentType(employee.employmentType)}</InfoChip>
          )}
          {employee.startDate && (
            <InfoChip icon={Calendar}>Since {formatDate(employee.startDate, 'monthYear')}</InfoChip>
          )}
        </div>

        {/* Manager — reports-to line with mini avatar */}
        {employee.manager && (
          <div className="flex items-center justify-center gap-2 pt-1 text-xs text-muted-foreground">
            <span className="opacity-70">Reports to</span>
            <span className="inline-flex items-center gap-1.5 font-medium text-foreground/80">
              <Avatar className="h-5 w-5 shrink-0">
                {employee.manager.avatarUrl && (
                  <AvatarImage src={employee.manager.avatarUrl} />
                )}
                <AvatarFallback className="text-[8px] bg-muted">
                  {getInitials(employee.manager.firstName, employee.manager.lastName)}
                </AvatarFallback>
              </Avatar>
              <span className="truncate">
                {formatName({ firstName: employee.manager.firstName, lastName: employee.manager.lastName })}
              </span>
            </span>
          </div>
        )}
      </div>

      {/* Footer - contact */}
      <div className="px-5 py-3 bg-muted/30 border-t border-border/50">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
            <CopyButton value={employee.workEmail} label={employee.workEmail} className="truncate text-xs" />
          </div>
          {/* Quick action buttons */}
          <div className="flex items-center gap-1 shrink-0">
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="h-8 w-8 rounded-full bg-hygge/10 hover:bg-hygge/20 text-hygge transition-colors cursor-pointer inline-flex items-center justify-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      const mailtoLink = document.createElement('a');
                      mailtoLink.href = `mailto:${employee.workEmail}`;
                      mailtoLink.click();
                    }}
                  >
                    <Mail className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Send email</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {employee.personalInfo?.phone ? (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href={`tel:${employee.personalInfo.phone}`}
                      className="h-8 w-8 rounded-full bg-hygge/10 hover:bg-hygge/20 text-hygge transition-colors cursor-pointer inline-flex items-center justify-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Phone className="h-4 w-4" />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent>{employee.personalInfo.phone}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className="h-8 w-8 rounded-full bg-muted/60 text-muted-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer inline-flex items-center justify-center"
                      disabled
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Phone className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>No phone</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton for loading state
 */
export function EmployeeCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      {/* Header skeleton */}
      <div className="px-6 pt-6 pb-4 bg-muted/30">
        <div className="flex flex-col items-center">
          <div className="h-20 w-20 rounded-full bg-muted animate-pulse" />
          <div className="mt-3 space-y-2 w-full flex flex-col items-center">
            <div className="h-5 w-32 rounded bg-muted animate-pulse" />
            <div className="h-4 w-24 rounded bg-muted animate-pulse" />
          </div>
        </div>
      </div>

      {/* Content skeleton */}
      <div className="px-5 py-4 space-y-3">
        <div className="flex items-center justify-center gap-3">
          <div className="h-4 w-20 rounded bg-muted animate-pulse" />
          <div className="h-4 w-16 rounded bg-muted animate-pulse" />
        </div>
        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border/50">
          <div className="h-4 w-20 rounded bg-muted animate-pulse" />
          <div className="h-4 w-16 rounded bg-muted animate-pulse" />
          <div className="h-4 w-24 rounded bg-muted animate-pulse" />
          <div className="h-4 w-20 rounded bg-muted animate-pulse" />
        </div>
      </div>

      {/* Footer skeleton */}
      <div className="px-5 py-3 bg-muted/30 border-t border-border/50">
        <div className="h-4 w-40 rounded bg-muted animate-pulse" />
      </div>
    </div>
  );
}
